import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import SaveDocButton from './SaveDocButton';
import {
  Loader2, Save, CheckCircle2, AlertTriangle, Sparkles, Calendar as CalendarIcon, Tag, Percent, Printer, Calculator, ChevronDown, ChevronRight, Layers, Pencil, Copy, Check,
} from 'lucide-react';
import { CalcShadowProvider, CalcShadowToggle, CalcShadow } from '@/components/CalcShadow';
import MaintenanceServiceDetailModal from '@/components/MaintenanceServiceDetailModal';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';
import { RoofM2ConfidenceBanner } from '@/components/RoofM2ConfidenceBanner';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';

/**
 * MaintenanceBuilderTab — costed quote builder for routine maintenance.
 *
 * Six services: Roof Treatment / House Wash / Roof Wash / Gutter Clean /
 * Driveway Clean / Driveway Treatment. Each is independently toggleable
 * with its own qty + rate + notes.
 *
 * Pricing layers (in this exact order):
 *   1. Σ qty × rate                            → subtotal
 *   2. minus multi-service bundle discount     → after_multi
 *   3. minus recurring schedule discount       → grand_total
 *
 * The backend is the source of truth for calc — this component only
 * mirrors the math live so the operator sees the impact while typing.
 */
export default function MaintenanceBuilderTab({ api, appraisalId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [flash, setFlash]     = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [selections, setSelections] = useState({});
  const [schedule, setSchedule]   = useState('one_off');
  const [notes, setNotes]         = useState('');
  const [snapshot, setSnapshot]   = useState(null);
  // Iter 111 — auto-fill qty hints sourced from Roof Details (roof_m2 /
  // perimeter_m / wall_m2 / driveway_m2). Keyed by service_key. Used
  // when a selection has no manually-entered qty.
  const [autoQty, setAutoQty]     = useState({});
  const [hintsSource, setHintsSource] = useState(null);
  const [hintsConfidence, setHintsConfidence] = useState(null);
  // Iter 115 — auto-picked size tier per service + auto-detected
  // surface from the appraisal's roof_subtype. The Tier picker
  // pre-selects from these; the operator can still override.
  const [suggestedTiers, setSuggestedTiers]     = useState({});
  const [suggestedSurface, setSuggestedSurface] = useState('');
  // Iter 116 — tier-rate catalogue keyed by (service_key, surface, tier).
  // The Detail modal seeds its back-calc fields from the matching row
  // so the operator sees the current persisted values, not zeros.
  const [tierRows, setTierRows] = useState({});
  // Detail-edit modal state — single hoisted modal so the overlay
  // renders at the parent level (avoids stacking with the schedule /
  // breakdown collapsibles).
  const [detailModal, setDetailModal] = useState({ open: false, serviceKey: null });
  const openServiceDetail = (svcKey) => setDetailModal({ open: true, serviceKey: svcKey });
  const closeServiceDetail = () => setDetailModal({ open: false, serviceKey: null });
  const [breakdown, setBreakdown] = useState(null);

  // Iter 100: pull the subtype-matched Stage 1A (Sterilise) + 1B (Wash)
  // breakdown so Roof Treatment + Roof Wash can show the same per-step
  // pricing model as Iron/Tin Tile. Auto-applies the m²-rate to the
  // rate field on the matching service when the operator clicks "Apply".
  const loadBreakdown = useCallback(async () => {
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/maintenance-builder/breakdown`);
      setBreakdown(data);
    } catch (e) {
      // Non-fatal — just hide the card.
    }
  }, [api, appraisalId]);
  useEffect(() => { loadBreakdown(); }, [loadBreakdown]);

  const applyBreakdownRate = (serviceKey, ratePerM2) => {
    setSelections((s) => ({ ...s, [serviceKey]: { ...(s[serviceKey] || {}), rate: ratePerM2, active: true } }));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [dRes, sRes, qRes, tRes] = await Promise.all([
          api.get('/maintenance-builder/defaults'),
          api.get(`/appraisals/${appraisalId}/maintenance-spec`),
          api.get(`/appraisals/${appraisalId}/maintenance-builder/auto-qty`),
          api.get('/maintenance-builder/size-tiers'),
        ]);
        if (!alive) return;
        setDefaults(dRes.data);
        const d = sRes.data || {};
        setSelections(d.selections || {});
        setSchedule(d.schedule || 'one_off');
        setNotes(d.notes || '');
        setSnapshot(d.calc_snapshot || null);
        setAutoQty(qRes.data?.services || {});
        setSuggestedTiers(qRes.data?.suggested_tiers || {});
        setSuggestedSurface(qRes.data?.suggested_surface || '');
        // Sprint S4 (2026-02c) — confidence banner metadata from the
        // 3-source resolver · surfaces where roof_m² came from.
        setHintsSource(qRes.data?.source || null);
        setHintsConfidence(qRes.data?.confidence || null);
        // Map tier rows by (service, surface, tier) for fast lookup.
        const map = {};
        for (const r of (tRes.data?.rows || [])) {
          map[`${r.service_key}::${r.surface || ''}::${r.tier}`] = r;
        }
        setTierRows(map);
      } catch (e) {
        setFlash({ tone: 'error', msg: _errMsg(e) });
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, appraisalId]);

  /** Iter 111 — resolve the live `selections` map into the payload
   *  shape we send to the backend (preview / save / override). For
   *  any *active* service whose `qty` is 0/empty, we substitute the
   *  auto-qty value pulled from Roof Details so the backend's
   *  _calc_spec sees the same effective quantity the operator sees
   *  on screen. Keeps backend deterministic — no FE auto-fill leak. */
  const resolveSelections = useCallback((src) => {
    const out = {};
    for (const [key, val] of Object.entries(src || {})) {
      if (!val) continue;
      const auto = Number(autoQty[key] || 0);
      const currentQty = Number(val.qty || 0);
      const needsAuto = !!val.active && currentQty <= 0 && auto > 0;
      out[key] = needsAuto ? { ...val, qty: auto } : val;
    }
    return out;
  }, [autoQty]);

  // Live preview as the operator types
  useEffect(() => {
    if (!defaults) return;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/appraisals/${appraisalId}/maintenance-spec/preview`,
          { selections: resolveSelections(selections), schedule },
          { signal: ctrl.signal }
        );
        setSnapshot(data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          // silent — keep the previous snapshot on transient errors
        }
      }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [selections, schedule, api, appraisalId, defaults, resolveSelections]);

  const updateService = (key, patch) => {
    setSelections((s) => ({ ...s, [key]: { ...(s[key] || {}), ...patch } }));
  };

  // v10.29 Sprint 2 · B2 (2026-02) — extract a user-readable string
  // from an axios error. Backend now returns structured detail objects
  // for maintenance-push failures ({code, message, error}); previously
  // catches printed `[object Object]`.
  const _errMsg = (e) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === 'object') return d.message || d.error || 'Save failed.';
    return d || e?.message || 'Save failed.';
  };

  const save = async () => {
    setSaving(true);
    setFlash(null);
    try {
      const { data } = await api.put(`/appraisals/${appraisalId}/maintenance-spec`, {
        selections: resolveSelections(selections), schedule, notes,
      });
      setSnapshot(data.calc_snapshot || snapshot);
      setFlash({ tone: 'ok', msg: 'Maintenance spec saved ✓' });
    } catch (e) {
      setFlash({ tone: 'error', msg: _errMsg(e) });
    } finally {
      setSaving(false);
    }
  };

  /** Iter 111 — write a `detail_override` block onto the selected
   *  service and immediately persist the maintenance spec so the
   *  override survives navigation. Activates the service if it
   *  wasn't already on. */
  const saveServiceOverride = async (serviceKey, body) => {
    // Filter null/empty/zero so we don't store noise on the doc.
    // Zeros come from the modal's default-seeded fields the operator
    // never touched — persisting them would bloat the override block
    // and visually flag the service as overridden when nothing real
    // was changed.
    const cleaned = {};
    for (const k of Object.keys(body || {})) {
      const v = body[k];
      if (v === null || v === undefined || v === '') continue;
      if (typeof v === 'number' && v === 0) continue;
      cleaned[k] = v;
    }
    const next = {
      ...selections,
      [serviceKey]: {
        ...(selections[serviceKey] || {}),
        active: true,
        detail_override: cleaned,
      },
    };
    setSelections(next);
    setSaving(true);
    setFlash(null);
    try {
      const { data } = await api.put(`/appraisals/${appraisalId}/maintenance-spec`, {
        selections: resolveSelections(next), schedule, notes,
      });
      setSnapshot(data.calc_snapshot || snapshot);
      setFlash({ tone: 'ok', msg: 'Service override saved ✓' });
    } catch (e) {
      setFlash({ tone: 'error', msg: _errMsg(e) });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  /** Iter 128 — clear the detail_override on a service and persist.
   *  One-click revert to the size-tier rate (used by the Reset button
   *  inside MaintenanceServiceDetailModal). */
  const resetServiceOverride = async (serviceKey) => {
    const cur = selections[serviceKey] || {};
    const { detail_override: _drop, ...rest } = cur;
    const next = { ...selections, [serviceKey]: rest };
    setSelections(next);
    setSaving(true);
    setFlash(null);
    try {
      const { data } = await api.put(`/appraisals/${appraisalId}/maintenance-spec`, {
        selections: resolveSelections(next), schedule, notes,
      });
      setSnapshot(data.calc_snapshot || snapshot);
      setFlash({ tone: 'ok', msg: 'Reverted to tier rate ✓' });
    } catch (e) {
      setFlash({ tone: 'error', msg: _errMsg(e) });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  /** Iter 111 — refresh defaults after a master-rate edit so the
   *  builder shows the new catalogue values immediately. Iter 116 —
   *  also re-pulls the size-tiers list so the Detail modal's seed
   *  values reflect the just-saved tier row. */
  const refreshDefaults = async () => {
    try {
      const [dRes, tRes] = await Promise.all([
        api.get('/maintenance-builder/defaults'),
        api.get('/maintenance-builder/size-tiers'),
      ]);
      setDefaults(dRes.data);
      const map = {};
      for (const r of (tRes.data?.rows || [])) {
        map[`${r.service_key}::${r.surface || ''}::${r.tier}`] = r;
      }
      setTierRows(map);
      setFlash({ tone: 'ok', msg: 'Master tier rate updated ✓' });
    } catch (e) {
      setFlash({ tone: 'error', msg: _errMsg(e) });
    }
  };

  const services = defaults?.services || [];
  const activeCount = useMemo(
    () => Object.values(selections).filter((v) => v?.active).length,
    [selections]
  );

  if (loading && !defaults) {
    return <div className="p-6 text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading maintenance builder…</div>;
  }

  return (
    <CalcShadowProvider defaultShow={true}>
    <div className="space-y-5" data-testid="maintenance-builder-tab">
      <div className="flex justify-end">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="maintenance-save-quote-pdf"
        />
      </div>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Sparkles size={18} className="text-cyan-600" /> Maintenance Builder
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Routine services — tick which to include, set quantities and the recurring schedule. Bundle and recurring discounts apply automatically.
          </p>
          {hintsConfidence?.roof_m2 && (
            <div className="mt-1.5">
              <RoofM2ConfidenceBanner
                api={api}
                appraisalId={appraisalId}
                source={hintsSource?.roof_m2}
                confidence={hintsConfidence.roof_m2}
                label="Roof m² qty hints"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="maintenance-save"
          onClick={save}
          disabled={saving}
          className="bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save spec
        </button>
        <button
          data-testid="maintenance-subscribe-save-pdf"
          onClick={() => {
            const url = `${api.defaults.baseURL}/maintenance/appraisals/${appraisalId}/quote.pdf`;
            window.open(url, '_blank');
          }}
          disabled={!snapshot || (snapshot.active_count || 0) === 0}
          title={(snapshot?.active_count || 0) === 0 ? 'Select at least one service first' : 'Open the one-page Subscribe & Save quote PDF'}
          className="bg-white border border-cyan-300 text-cyan-800 hover:bg-cyan-50 text-sm font-semibold rounded-md px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
        >
          <Printer size={13} /> Subscribe &amp; Save PDF
        </button>
        <CalcShadowToggle testid="maintenance-calcs-toggle" />
        </div>
      </div>

      {/* Iter167 — Roof Details quick-edit panel under the Maintenance banner. */}
      <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={() => { try { window.dispatchEvent(new CustomEvent('rtr:roof-details-applied')); } catch (_) { /* noop */ } }} />

      {flash && (
        <div data-testid="maintenance-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* SERVICES */}
        <div className="lg:col-span-2 space-y-3">
          {breakdown && (breakdown.treatment || breakdown.wash) && (
            <BreakdownCard breakdown={breakdown} onApply={applyBreakdownRate} />
          )}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="bg-slate-100 px-3 py-2 text-xs uppercase tracking-wide font-bold text-slate-600">
              Services ({activeCount} selected)
            </div>
            <div className="divide-y divide-slate-100">
              {services.map((svc) => {
                const sel = selections[svc.key] || {};
                const active = !!sel.active;
                // Iter 115 — Tier catalogue for this service drives the
                // new primary pricing model (flat-rate × condition mod).
                const tierCfg  = defaults?.tier_catalogue?.[svc.key];
                const surfaces = tierCfg?.surfaces; // null when service has no surface split
                const tiers    = tierCfg?.tiers || [];
                const suggestion = suggestedTiers[svc.key] || {};
                const surface  = sel.surface || (surfaces ? (suggestedSurface || surfaces[0]?.key) : '');
                const tier     = sel.tier    || suggestion.tier || '';
                const inRange  = suggestion.in_range !== false;
                const condition = sel.condition || 'normal';

                const hasOverride = !!(sel.detail_override && Object.keys(sel.detail_override).length > 0);

                // Live snap line — the backend's calc is the source of
                // truth for the displayed total (tier base + condition
                // delta, or override, etc.).
                const snapLine = (snapshot?.lines || []).find(l => l.service_key === svc.key);
                const lineTotal = active ? Number(snapLine?.total || 0) : 0;
                const tierBase  = Number(snapLine?.tier_base_rate || 0);
                const condDelta = Number(snapLine?.condition_delta || 0);
                const lineMode  = snapLine?.mode || 'tier';

                return (
                  <div
                    key={svc.key}
                    className={`p-3 transition-colors ${active ? 'bg-cyan-50/30' : 'bg-white'}`}
                    data-testid={`maintenance-service-${svc.key}`}
                  >
                    <div className="flex items-start gap-3 flex-wrap">
                      <label className="flex items-center gap-2 cursor-pointer pt-1">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={(e) => updateService(svc.key, {
                            active: e.target.checked,
                            // Auto-seed tier + surface on activation
                            ...(e.target.checked && !sel.tier && suggestion.tier ? { tier: suggestion.tier } : {}),
                            ...(e.target.checked && !sel.surface && surfaces && suggestedSurface ? { surface: suggestedSurface } : {}),
                          })}
                          className="w-4 h-4 accent-cyan-600"
                          data-testid={`maintenance-toggle-${svc.key}`}
                        />
                        <div>
                          <div className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                            {svc.label}
                            {hasOverride && (
                              <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-800 border border-amber-300"
                                    data-testid={`maintenance-override-badge-${svc.key}`}
                                    title="CUSTOM rate — this service uses a per-appraisal override.

The size-tier auto-pricing (Small / Medium / Large) is bypassed, so this appraisal's rate stays at whatever you saved in the Detail modal — even if the master tier table changes later.

To revert, open the Detail modal and click 'Reset to tier rate'.">Custom</span>
                            )}
                            {active && tier && lineMode === 'tier' && (
                              <TierBadge svcKey={svc.key} svcLabel={svc.label} unit={tierCfg?.unit}
                                         tier={tier} surface={surface}
                                         tierRow={tierRows[`${svc.key}::${surface || ''}::${tier}`]}
                                         tierBase={tierBase} condDelta={condDelta}
                                         condition={condition} lineTotal={lineTotal} />
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">{svc.description}</div>
                        </div>
                      </label>
                      <button
                        type="button"
                        data-testid={`maintenance-edit-detail-${svc.key}`}
                        onClick={() => openServiceDetail(svc.key)}
                        title="Edit Buy / Materials / Labour / Contractor breakdown + back-calculate from Job Total (same shape as Stage 2 Paint)"
                        className={`px-2 py-1 text-[11px] font-semibold rounded border self-center inline-flex items-center gap-1 ${
                          hasOverride
                            ? 'text-amber-700 hover:text-amber-900 hover:bg-amber-50 border-amber-300 bg-amber-50/60'
                            : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 border-emerald-300'
                        }`}
                      >
                        <Pencil size={11} /> {hasOverride ? 'Custom' : 'Detail'}
                      </button>

                      <div className="ml-auto flex flex-col gap-1">
                        <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Line total</span>
                        <div
                          data-testid={`maintenance-line-${svc.key}`}
                          className={`px-3 py-1.5 text-base font-mono-num font-bold rounded border border-dashed min-w-[110px] text-right ${active ? 'bg-cyan-50 text-cyan-900 border-cyan-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                        >
                          ${lineTotal.toFixed(2)}
                        </div>
                        {active && lineMode === 'tier' && tierBase > 0 && (
                          <CalcShadow testid={`maintenance-line-calc-${svc.key}`}>
                            ${tierBase.toFixed(2)} (base{condDelta !== 0 ? ` ${condDelta > 0 ? '+' : ''}${condDelta.toFixed(2)} (${condition})` : ''}) = <strong>${lineTotal.toFixed(2)}</strong>
                          </CalcShadow>
                        )}
                      </div>
                    </div>

                    {/* Iter 115 — Tier / Surface / Condition pickers */}
                    {active && tierCfg && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-3"
                           data-testid={`maintenance-tier-picker-${svc.key}`}>
                        {/* Surface (only for services that split by surface) */}
                        {surfaces && (
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500 mb-1">
                              Surface{suggestedSurface && surface === suggestedSurface && (
                                <span className="ml-1 text-emerald-600 normal-case font-semibold">· auto</span>
                              )}
                            </div>
                            <select value={surface || ''}
                                    onChange={(e) => updateService(svc.key, { surface: e.target.value })}
                                    data-testid={`maintenance-surface-${svc.key}`}
                                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded">
                              {surfaces.map(sf => (
                                <option key={sf.key} value={sf.key}>{sf.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {/* Tier S/M/L */}
                        <div className={surfaces ? '' : 'md:col-span-2'}>
                          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                            Size tier ({tierCfg.unit})
                            {!inRange && (
                              <span className="text-amber-700 normal-case font-semibold"
                                    title={`Roof dim ${suggestion.qty?.toFixed?.(0) || '?'} ${tierCfg.unit} falls outside the catalogued tier ranges — verify before quoting.`}
                                    data-testid={`maintenance-tier-out-of-range-${svc.key}`}>
                                ⚠ out-of-range
                              </span>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {tiers.map(t => {
                              const picked = tier === t.key;
                              const isAuto = suggestion.tier === t.key;
                              return (
                                <button key={t.key} type="button"
                                        data-testid={`maintenance-tier-${svc.key}-${t.key}`}
                                        onClick={() => updateService(svc.key, { tier: t.key })}
                                        title={`${t.min_dim}–${t.max_dim} ${tierCfg.unit}`}
                                        className={`flex-1 px-2 py-1 text-xs font-bold rounded border transition-colors ${
                                          picked
                                            ? 'bg-cyan-600 text-white border-cyan-600'
                                            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                                        }`}>
                                  {t.label}{isAuto && !picked && <span className="ml-1 text-[8px] opacity-70">auto</span>}
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Live qty: {(suggestion.qty || 0).toFixed(1)} {tierCfg.unit}
                          </div>
                        </div>
                        {/* Condition modifier */}
                        <div>
                          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500 mb-1">Surface condition</div>
                          <div className="flex gap-1">
                            {(defaults?.condition_options || []).map(c => {
                              const picked = condition === c.key;
                              const tone = c.key === 'heavily_dirty'
                                ? 'bg-rose-600 border-rose-600'
                                : c.key === 'semi_clean'
                                    ? 'bg-emerald-600 border-emerald-600'
                                    : 'bg-slate-600 border-slate-600';
                              return (
                                <button key={c.key} type="button"
                                        data-testid={`maintenance-condition-${svc.key}-${c.key}`}
                                        onClick={() => updateService(svc.key, { condition: c.key })}
                                        title={c.pct !== 0 ? `${c.pct > 0 ? '+' : ''}${c.pct}% of tier base` : 'No modifier'}
                                        className={`flex-1 px-1.5 py-1 text-[11px] font-bold rounded border transition-colors ${
                                          picked
                                            ? `${tone} text-white`
                                            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                                        }`}>
                                  {c.label}{c.pct !== 0 ? ` ${c.pct > 0 ? '+' : ''}${c.pct}%` : ''}
                                </button>
                              );
                            })}
                          </div>
                          {condition !== 'normal' && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-[9px] text-slate-500 font-semibold">Override %</span>
                              <input type="number" step="1" value={sel.condition_pct ?? ''}
                                     placeholder={String(defaults?.condition_options?.find(c => c.key === condition)?.pct ?? 0)}
                                     onChange={(e) => updateService(svc.key, { condition_pct: e.target.value === '' ? null : Number(e.target.value) })}
                                     data-testid={`maintenance-condition-pct-${svc.key}`}
                                     className="w-14 px-1 py-0.5 text-[11px] border border-slate-300 rounded text-right font-mono-num" />
                              <span className="text-[9px] text-slate-500 font-semibold">or flat $</span>
                              <input type="number" step="0.01" value={sel.condition_flat ?? ''}
                                     placeholder="0"
                                     onChange={(e) => updateService(svc.key, { condition_flat: e.target.value === '' ? null : Number(e.target.value) })}
                                     data-testid={`maintenance-condition-flat-${svc.key}`}
                                     className="w-16 px-1 py-0.5 text-[11px] border border-slate-300 rounded text-right font-mono-num" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {active && (
                      <input
                        type="text"
                        placeholder="Notes — access details, frequency overrides, special instructions…"
                        value={sel.notes || ''}
                        onChange={(e) => updateService(svc.key, { notes: e.target.value })}
                        data-testid={`maintenance-notes-${svc.key}`}
                        className="mt-2 w-full px-2 py-1 text-xs border border-slate-200 rounded"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SCHEDULE */}
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-2 mb-2">
              <CalendarIcon size={13} className="text-cyan-600" />
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Schedule</span>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {(defaults?.schedule_options || []).map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`maintenance-schedule-${opt.key}`}
                  onClick={() => setSchedule(opt.key)}
                  className={`text-xs font-bold px-2.5 py-1 rounded-md border transition-colors ${
                    schedule === opt.key
                      ? 'bg-cyan-600 text-white border-cyan-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                  }`}
                >
                  {opt.label}{opt.pct > 0 && <span className="ml-1 opacity-80">−{opt.pct}%</span>}
                </button>
              ))}
            </div>
            <textarea
              placeholder="Free-text notes for the operator / customer — frequency, access keys, gate codes…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="maintenance-notes"
              className="mt-3 w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              rows={2}
            />
          </div>
        </div>

        {/* SUMMARY */}
        <SummaryPanel snapshot={snapshot} discounts={defaults?.multi_service_discounts || []} />
        {snapshot && <QuoteTotalStrip api={api} appraisalId={appraisalId} linesTotal={snapshot.grand_total} testid="maintenance-quote-total-strip" />}
      </div>

      {/* Iter 111 — service detail modal (paint-modal visual format).
          Renders here at parent level so the overlay sits above the
          breakdown + schedule cards. */}
      {detailModal.open && detailModal.serviceKey && (() => {
        const svc = services.find((s) => s.key === detailModal.serviceKey);
        if (!svc) return null;
        const sel = selections[svc.key] || {};
        // Iter 116 — pass tier/surface/tierRow so the modal can seed
        // its back-calc fields from the persisted master tier row and
        // hit the tier PATCH endpoint when saving to master.
        const suggestion = suggestedTiers[svc.key] || {};
        const tierCfg    = defaults?.tier_catalogue?.[svc.key];
        const surfaces   = tierCfg?.surfaces;
        const tier       = sel.tier    || suggestion.tier || '';
        const surface    = sel.surface || (surfaces ? (suggestedSurface || surfaces[0]?.key) : '');
        const tierRow    = tierRows[`${svc.key}::${surface || ''}::${tier}`];
        return (
          <MaintenanceServiceDetailModal
            api={api}
            open={detailModal.open}
            onOpenChange={(o) => (o ? null : closeServiceDetail())}
            service={svc}
            override={sel.detail_override || {}}
            tier={tier}
            surface={surface}
            tierRow={tierRow}
            onSaveOverride={(body) => saveServiceOverride(svc.key, body)}
            onMasterSaved={refreshDefaults}
            onResetOverride={() => resetServiceOverride(svc.key)}
          />
        );
      })()}
    </div>
    </CalcShadowProvider>
  );
}

function SummaryPanel({ snapshot, discounts }) {
  if (!snapshot) return null;
  const s = snapshot;
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden h-fit lg:sticky lg:top-4" data-testid="maintenance-summary">
      <div className="bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wide font-bold">Quote summary</div>
      <div className="p-3 space-y-2 text-sm">
        <Row label="Active services" value={s.active_count} />
        <Row label="Subtotal" value={`$${s.subtotal.toFixed(2)}`} mono />
        {s.multi_discount?.pct > 0 && (
          <Row
            label={<span className="inline-flex items-center gap-1 text-emerald-700"><Tag size={11} /> {s.multi_discount.label}</span>}
            value={`−$${s.multi_discount.amount.toFixed(2)}`}
            mono
            tone="emerald"
            testid="maintenance-summary-multi"
          />
        )}
        {s.schedule?.pct > 0 && (
          <Row
            label={<span className="inline-flex items-center gap-1 text-emerald-700"><Percent size={11} /> {s.schedule.label} (−{s.schedule.pct}%)</span>}
            value={`−$${s.schedule.amount.toFixed(2)}`}
            mono
            tone="emerald"
            testid="maintenance-summary-schedule"
          />
        )}
        <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
          <span className="text-sm font-bold text-slate-800">Grand total</span>
          <span data-testid="maintenance-summary-grand-total" className="font-mono-num font-extrabold text-lg text-cyan-700">${s.grand_total.toFixed(2)}</span>
        </div>
        <CalcShadow testid="maintenance-summary-grand-total-calc" tone="emerald">
          Σ ${s.subtotal.toFixed(2)}{s.multi_discount?.pct > 0 ? ` − $${s.multi_discount.amount.toFixed(2)} (${s.multi_discount.pct}%)` : ''}{s.schedule?.pct > 0 ? ` − $${s.schedule.amount.toFixed(2)} (${s.schedule.label} ${s.schedule.pct}%)` : ''} = <strong>${s.grand_total.toFixed(2)}</strong>
        </CalcShadow>
      </div>
      {discounts.length > 0 && (
        <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-[11px] text-amber-900">
          <div className="font-bold mb-0.5">Bundle discount tiers</div>
          <ul className="space-y-0.5">
            {discounts.map((d) => (
              <li key={d.pct}>· {d.label}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, tone, testid }) {
  const toneCls = tone === 'emerald' ? 'text-emerald-700' : 'text-slate-700';
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={`text-xs ${toneCls}`}>{label}</span>
      <span className={`text-sm ${mono ? 'font-mono-num font-bold' : ''} ${toneCls}`}>{value}</span>
    </div>
  );
}

/**
 * Iter 100: BreakdownCard — surfaces the subtype-matched Stage 1A
 * (Sterilise) + 1B (Wash) coats from the Restoration paint-system
 * catalogue. The operator clicks "Apply to Roof Treatment" / "Apply
 * to Roof Wash" to copy the per-m² rate into the matching service
 * row. Editing the breakdown itself happens in the Restoration tab's
 * per-coat editor — this card is read-only here to avoid duplicate
 * sources of truth.
 */
function BreakdownCard({ breakdown, onApply }) {
  const [open, setOpen] = useState(true);
  if (!breakdown) return null;
  const { subtype, paint_system_name, fallback, treatment, wash } = breakdown;
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-md overflow-hidden" data-testid="maintenance-breakdown-card">
      <button type="button" onClick={() => setOpen(o => !o)}
              data-testid="maintenance-breakdown-toggle"
              className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-emerald-100/30">
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-emerald-700" /> : <ChevronRight size={14} className="text-emerald-700" />}
          <Layers size={13} className="text-emerald-700" />
          <span className="text-sm font-bold text-emerald-900">Cost breakdown from <strong>{paint_system_name}</strong></span>
          {fallback && <span className="text-[10px] uppercase tracking-wide font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5">Generic fallback · {subtype || '—'}</span>}
        </span>
        <span className="text-[11px] text-emerald-700 italic">Mirrors Stage 1A / 1B from Restoration — edit per-step in the Restoration tab.</span>
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-emerald-200">
          <BreakdownStep coat={treatment} title="Roof Treatment (Stage 1A · Sterilise)" colour="emerald"
                          onApply={() => onApply('roof_treatment', treatment?.rate_per_m2 || 0)}
                          applyLabel="Apply to Roof Treatment" testid="maintenance-breakdown-treatment" />
          <BreakdownStep coat={wash} title="Roof Wash (Stage 1B · Wash)" colour="cyan"
                          onApply={() => onApply('roof_wash', wash?.rate_per_m2 || 0)}
                          applyLabel="Apply to Roof Wash" testid="maintenance-breakdown-wash" />
        </div>
      )}
    </div>
  );
}

function BreakdownStep({ coat, title, colour, onApply, applyLabel, testid }) {
  if (!coat) return (
    <div className="bg-white p-3 text-xs text-slate-500 italic" data-testid={`${testid}-none`}>
      <div className="font-bold text-slate-700 not-italic mb-1">{title}</div>
      No matching coat found in the selected paint system.
    </div>
  );
  const isHourly = coat.supply_mode === 'hourly';
  const btnCls = colour === 'cyan' ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-emerald-600 hover:bg-emerald-700';
  const rateCls = colour === 'cyan' ? 'text-cyan-700' : 'text-emerald-700';
  return (
    <div className="bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-bold text-slate-700">{title}</div>
        <div className={`text-base font-mono-num font-bold ${rateCls}`} data-testid={`${testid}-rate`}>${coat.rate_per_m2.toFixed(2)}/m²</div>
      </div>
      <div className="text-[11px] text-slate-500 italic mb-2">{coat.desc || coat.label}</div>
      <div className="grid grid-cols-4 gap-1.5 text-[11px] mb-2">
        {[
          { k: 'buy_per_m2', label: 'Buy' },
          { k: 'material_per_m2', label: 'Material' },
          { k: 'labour_per_m2', label: 'Labour' },
          { k: 'contractor_per_m2', label: 'Contractor' },
        ].map(({ k, label }) => (
          <div key={k} className="bg-slate-50 border border-slate-200 rounded px-1.5 py-1">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-mono-num font-semibold text-slate-800">${coat[k].toFixed(2)}</div>
          </div>
        ))}
      </div>
      {isHourly && (
        <div className="text-[10px] text-slate-500 italic mb-2">
          Supply mode: <strong>hourly</strong> · ${coat.material_hourly_rate}/hr × {coat.apply_hours} hr / {coat.apply_reference_m2} m²
        </div>
      )}
      <button type="button" onClick={onApply}
              data-testid={`${testid}-apply`}
              className={`text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded inline-flex items-center gap-1 ${btnCls} text-white`}>
        <Calculator size={11} /> {applyLabel}
      </button>
    </div>
  );
}



/**
 * Iter 117 — TierBadge with hover-popup tooltip.
 *
 * Shows a compact `{tier} · {surface}` chip on the service row. On
 * hover, expands into a popup card that breaks down the persisted
 * master tier rate: materials cost + hours × labour $/hr + hours
 * × contractor $/hr ≈ flat_rate. Foremen can sanity-check pricing
 * without opening the back-calc modal.
 */
function TierBadge({ svcKey, svcLabel, unit, tier, surface, tierRow,
                     tierBase, condDelta, condition, lineTotal }) {
  const [hover, setHover] = useState(false);
  const [copied, setCopied] = useState(false);
  const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const hours = Number(tierRow?.labour_hours || 0);
  const mat   = Number(tierRow?.materials_cost || 0);
  const lhr   = Number(tierRow?.labour_rate_per_hr || 0);
  const chr   = Number(tierRow?.contractor_rate_per_hr || 0);
  const labourSub     = hours * lhr;
  const contractorSub = hours * chr;
  const buildSum      = mat + labourSub + contractorSub;
  // Native-tooltip fallback so the breakdown is reachable on mobile
  // and via accessibility readers even when the popup card is closed.
  const surfaceLabel = surface ? surface.replace(/_/g, ' ') : '';
  const nativeTitle = tierRow
    ? `${svcLabel} · ${tier}${surfaceLabel ? ' · ' + surfaceLabel : ''} = ${fmt(tierRow.flat_rate)}\n`
      + `Materials ${fmt(mat)} + ${hours.toFixed(2)} hr × ${fmt(lhr)}/hr labour + ${hours.toFixed(2)} hr × ${fmt(chr)}/hr contractor`
    : `${svcLabel} · ${tier}${surfaceLabel ? ' · ' + surfaceLabel : ''}`;
  // Iter 118 — structured plain-text breakdown the operator can paste
  // straight into a quick reply email / SMS to the client.
  const buildClipboardText = () => {
    const lines = [];
    const header = `${svcLabel} · ${tier}${surfaceLabel ? ' · ' + surfaceLabel : ''} = ${fmt(lineTotal)}`;
    lines.push(header);
    if (tierRow) {
      lines.push(`  Materials cost                  ${fmt(mat)}`);
      lines.push(`  ${hours.toFixed(2)} hr × ${fmt(lhr)}/hr labour      ${fmt(labourSub)}`);
      lines.push(`  ${hours.toFixed(2)} hr × ${fmt(chr)}/hr contractor  ${fmt(contractorSub)}`);
      lines.push(`  Master tier flat rate           ${fmt(tierRow.flat_rate)}`);
    }
    if (condition !== 'normal' && condDelta !== 0) {
      const sign = condDelta > 0 ? '+' : '';
      lines.push(`  ${condition.replace(/_/g, ' ')} modifier         ${sign}${fmt(condDelta)}`);
    }
    return lines.join('\n');
  };
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildClipboardText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API blocked — fall back to a textarea trick so the
      // button still works in older browsers / iframed previews.
      const ta = document.createElement('textarea');
      ta.value = buildClipboardText();
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1800); }
      finally { document.body.removeChild(ta); }
    }
  };
  return (
    <span className="relative inline-flex" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-cyan-100 text-cyan-800 border border-cyan-300 cursor-help"
            data-testid={`maintenance-tier-badge-${svcKey}`}
            title={nativeTitle}>
        {tier}{surfaceLabel ? ` · ${surfaceLabel}` : ''}
      </span>
      {hover && tierRow && (
        <div className="absolute left-0 top-full mt-1 z-30 w-80 bg-white border border-cyan-300 rounded-md shadow-xl p-3 text-xs normal-case tracking-normal font-normal text-slate-700"
             data-testid={`maintenance-tier-tooltip-${svcKey}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-bold text-cyan-800 text-sm">
              {svcLabel} · {tier}{surfaceLabel ? ` · ${surfaceLabel}` : ''}
              <span className="ml-2 font-mono-num text-emerald-700">{fmt(tierRow.flat_rate)}</span>
            </div>
            <button type="button" onClick={onCopy}
                    data-testid={`maintenance-tier-copy-${svcKey}`}
                    className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded inline-flex items-center gap-1 border transition-colors ${
                      copied ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                             : 'bg-slate-50 text-slate-600 border-slate-300 hover:bg-cyan-50 hover:text-cyan-700 hover:border-cyan-300'
                    }`}
                    title="Copy quote breakdown to clipboard">
              {copied
                ? <><Check size={10} /> Copied</>
                : <><Copy size={10} /> Copy</>}
            </button>
          </div>
          <div className="text-[10px] text-slate-500 mb-2">
            Master tier rate · range {tierRow.min_dim}–{tierRow.max_dim} {unit || ''}
          </div>
          <table className="w-full text-[11px]">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-1">Materials cost</td>
                <td className="py-1 text-right font-mono-num text-slate-800">{fmt(mat)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1">{hours.toFixed(2)} hr × {fmt(lhr)}/hr labour</td>
                <td className="py-1 text-right font-mono-num text-slate-800">{fmt(labourSub)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1">{hours.toFixed(2)} hr × {fmt(chr)}/hr contractor</td>
                <td className="py-1 text-right font-mono-num text-slate-800">{fmt(contractorSub)}</td>
              </tr>
              <tr>
                <td className="pt-1 font-bold text-slate-700">Builds to</td>
                <td className="pt-1 text-right font-mono-num font-bold text-slate-800">{fmt(buildSum)}</td>
              </tr>
            </tbody>
          </table>
          {Math.abs(buildSum - Number(tierRow.flat_rate || 0)) > 0.5 && (
            <div className="mt-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
                 data-testid={`maintenance-tier-tooltip-mismatch-${svcKey}`}>
              ⚠ Materials + labour + contractor adds to {fmt(buildSum)} but the flat rate is {fmt(tierRow.flat_rate)} — the back-calc may need a refresh in the Detail modal.
            </div>
          )}
          {(condDelta !== 0 || condition !== 'normal') && (
            <div className="mt-2 pt-2 border-t border-slate-200 text-[11px]">
              <div className="flex justify-between text-slate-600">
                <span>Base (Normal)</span>
                <span className="font-mono-num">{fmt(tierBase)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>+ {condition.replace(/_/g, ' ')} modifier</span>
                <span className="font-mono-num">{condDelta > 0 ? '+' : ''}{fmt(condDelta)}</span>
              </div>
              <div className="flex justify-between font-bold text-cyan-800 mt-0.5">
                <span>This appraisal</span>
                <span className="font-mono-num">{fmt(lineTotal)}</span>
              </div>
            </div>
          )}
        </div>
      )}
      {hover && !tierRow && (
        <div className="absolute left-0 top-full mt-1 z-30 w-72 bg-white border border-amber-300 rounded-md shadow-xl p-3 text-[11px] normal-case tracking-normal font-normal text-amber-800"
             data-testid={`maintenance-tier-tooltip-${svcKey}`}>
          No master tier row stored yet for this {svcLabel.toLowerCase()} · {tier}{surfaceLabel ? ' · ' + surfaceLabel : ''} combination — open <strong>Detail</strong> and Apply to size tier to seed it.
        </div>
      )}
    </span>
  );
}
